import { Grid2, Typography } from "@mui/material";
import { useBlocker, useSearchParams, useNavigate } from "react-router-dom";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import React, {
    useCallback, useEffect, useMemo, useState,
} from "react";
import { useTranslate } from "../../hooks/useTranslate";
import BaseDialog from "../../components/BaseDialog";
import "./AddStudent.css";
import { DetailsForm } from "./components/DetailsForm";
import { TimezoneSelect } from "./components/TimezoneSelect";
import { YearGroupSelect } from "./components/YearGroupSelect";
import { PackageSubjectsSelect } from "./components/PackageSubjectsSelect";
import subjectSelectionService from "../../services/subjectSelectionService";
import { HigherSubjectsSelect } from "./components/HigherSubjectsSelect";
import { Confirmation } from "./components/Confirmation";
import { STEPS, useAddStudent } from "./hooks/useAddStudent";
import { useStudents } from "../../context/students";
import Preloader from "../../components/Preloader";
import { Complete } from "./components/Complete";
import useUserAuth from "../../auth/hooks/useUserAuth";
import { STUDENT_STATUS } from "../../constants/student.status";

const TIME_REMAINING = {
    DETAILS: 5,
    TIMEZONE: 4,
    YEAR_GROUP: 3,
    SUBJECTS: 2,
    HIGHER_SUBJECTS: 2,
    CONFIRMATION: 1,
};

const AddStudent = () => {
    const [searchParams] = useSearchParams();
    const fromPathname = searchParams.get("from");

    const { t } = useTranslate();
    const navigate = useNavigate();
    const { azureAccessToken, refetchUserToken } = useUserAuth();
    const { students, onChangeActiveStudentId } = useStudents();

    const studentsLength = useMemo(() => students?.length, [students?.length]);

    const {
        step,
        isEditMode,
        isSessionSet,
        isLoadingData,

        isComplete,

        registrationId,
        sessionKey,
        divisionSelected,
        yearGroupSelected,
        packageSelected,
        subjectsSelected,
        detailsData,
        setStep,
        setRegistrationId,
        setDivisionSelected,
        setSessionKey,
        setYearGroupSelected,
        setPackageSelected,
        setSubjectsSelected,
        setDetailsData,
        clearStoredData,
        hasStoredData,
        isClearing,
    } = useAddStudent();

    useEffect(() => {
        if (isComplete && azureAccessToken) refetchUserToken();
    }, [isComplete, azureAccessToken]);

    const studentName = detailsData?.forename || "";
    const foundNewStudent = useMemo(() => students.find((s) => s.forename === detailsData?.forename && s.surname === detailsData?.surname), [students, detailsData?.forename, detailsData?.surname]);

    const [showCancelDialog, setShowCancelDialog] = useState(false);

    const progress = useMemo(() => {
        if (isComplete) return 100;
        if (isEditMode) return 95;
        if (step === STEPS.DETAILS) return 15;
        if (step === STEPS.TIMEZONE) return 35;
        if (step === STEPS.YEAR_GROUP) return 50;
        if (step === STEPS.SUBJECTS) return 80;
        if (step === STEPS.HIGHER_SUBJECTS) return 80;
        if (step === STEPS.CONFIRMATION) return 90;
        return 0;
    }, [step, isEditMode, isComplete]);

    const shouldBlock = useCallback(({ currentLocation, nextLocation }) => {
        if (currentLocation.pathname === nextLocation.pathname) return false;
        return !isClearing && hasStoredData;
    }, [isClearing, hasStoredData]);

    const blocker = useBlocker(shouldBlock);

    const hasHigherSubjects = useMemo(() => (packageSelected ? subjectSelectionService.hasHigherSubjects(packageSelected, subjectsSelected) : false), [packageSelected, packageSelected, subjectsSelected]);

    const cancelButtonLink = useMemo(() => {
        if ((fromPathname || "").includes("alumni")) return "/alumni";
        const studentToGoBack = foundNewStudent || students?.[0];

        if (!studentToGoBack) return "/";
        if (studentToGoBack?.studentStatusCode === STUDENT_STATUS.STUDENT) return `/home/${studentToGoBack?.studentId}`;
        return `/dashboard/${studentToGoBack?.studentId}`;
    }, [fromPathname, foundNewStudent, students]);

    const onCancelClick = () => {
        const studentToGoBack = foundNewStudent || students?.[0];

        onChangeActiveStudentId(studentToGoBack?.studentId);
        navigate(cancelButtonLink);
    };

    const onCancelDialogClose = () => {
        try {
            blocker.reset?.();
            setShowCancelDialog(false);
        } catch {
            // silently fails
        }
    };
    const onCancelDialogContinue = () => {
        try {
            blocker.proceed?.();
            setTimeout(() => {
                clearStoredData(true);
            }, 0);
        } catch {
            // silently fails
        }
    };

    return (
        <div className="page-content add-student-page">
            <Grid2 container spacing={2} className="mb-12">
                <Grid2 size={{ xs: 12 }}>
                    {!isComplete && (
                        <Button
                            variant="text"
                            onClick={onCancelClick}
                            className="add-student-cancel-button"
                        >
                            {t("addStudent.cancelAdding")}
                        </Button>
                    )}
                    <Typography variant="h4" className="text-primary mt-4">
                        {isComplete ? t("addStudent.processComplete") : t("addStudent.estimatedRemainingTime", { time: TIME_REMAINING[step] })}
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        className="add-student-progress"
                    />
                </Grid2>
            </Grid2>
            <Preloader isLoading={isLoadingData || (registrationId && !isSessionSet)}>
                {isComplete && (
                    <Complete
                        homeLink={`/home/${students?.[0]?.studentId}`}
                        clearStoredData={clearStoredData}
                    />
                )}
                {step === STEPS.DETAILS && (
                    <DetailsForm
                        detailsData={detailsData}
                        onDetailsDataChange={setDetailsData}
                        registrationId={registrationId}
                        onRegistrationIdChange={setRegistrationId}
                        sessionKey={sessionKey}
                        onSessionKeyChange={setSessionKey}
                        onNext={() => setStep(isEditMode ? STEPS.CONFIRMATION : STEPS.TIMEZONE)}
                    />
                )}
                {step === STEPS.TIMEZONE && (
                    <TimezoneSelect
                        registrationId={registrationId}
                        detailsData={detailsData}
                        divisionSelected={divisionSelected}
                        onDivisionChange={setDivisionSelected}
                        onPrev={() => setStep(isEditMode ? STEPS.CONFIRMATION : STEPS.DETAILS)}
                        onNext={() => setStep(STEPS.YEAR_GROUP)}
                    />
                )}
                {step === STEPS.YEAR_GROUP && (
                    <YearGroupSelect
                        registrationId={registrationId}
                        childId={detailsData?.childId}
                        studentName={studentName}
                        divisionSelected={divisionSelected}
                        yearGroupSelected={yearGroupSelected}
                        onYearGroupChange={setYearGroupSelected}
                        onPrev={() => setStep(isEditMode ? STEPS.CONFIRMATION : STEPS.TIMEZONE)}
                        onNext={() => setStep(STEPS.SUBJECTS)}
                    />
                )}
                {step === STEPS.SUBJECTS && (
                    <PackageSubjectsSelect
                        registrationId={registrationId}
                        childId={detailsData?.childId}
                        studentName={studentName}
                        studySchedule={detailsData?.studySchedule}
                        desiredStartDate={detailsData?.desiredStartDate}
                        divisionSelected={divisionSelected}
                        yearGroupSelected={yearGroupSelected?.yearGroupId}
                        packageSelected={packageSelected}
                        onPackageChange={setPackageSelected}
                        subjectsSelected={subjectsSelected}
                        onSubjectChange={setSubjectsSelected}
                        onPrev={() => setStep(isEditMode ? STEPS.CONFIRMATION : STEPS.YEAR_GROUP)}
                        onNext={() => setStep(hasHigherSubjects ? STEPS.HIGHER_SUBJECTS : STEPS.CONFIRMATION)}
                    />
                )}
                {step === STEPS.HIGHER_SUBJECTS && (
                    <HigherSubjectsSelect
                        registrationId={registrationId}
                        childId={detailsData?.childId}
                        studySchedule={detailsData?.studySchedule}
                        desiredStartDate={detailsData?.desiredStartDate}
                        divisionSelected={divisionSelected}
                        yearGroupSelected={yearGroupSelected?.yearGroupId}
                        packageSelected={packageSelected}
                        onPackageChange={setPackageSelected}
                        subjectsSelected={subjectsSelected}
                        onSubjectChange={setSubjectsSelected}
                        onPrev={() => setStep(STEPS.SUBJECTS)}
                        onNext={() => setStep(STEPS.CONFIRMATION)}
                    />
                )}
                {step === STEPS.CONFIRMATION && (
                    <Confirmation
                        registrationId={registrationId}
                        studentsLength={studentsLength}
                        childId={detailsData?.childId}
                        studySchedule={detailsData?.studySchedule}
                        divisionSelected={divisionSelected}
                        detailsData={detailsData}
                        yearGroupSelected={yearGroupSelected}
                        packageSelected={packageSelected}
                        subjectsSelected={subjectsSelected}
                        onPackageChange={setPackageSelected}
                        setStep={setStep}
                        onPrev={() => setStep(STEPS.SUBJECTS)}
                        onNext={() => {}}
                    />
                )}
            </Preloader>
            <BaseDialog
                open={showCancelDialog || blocker.state === "blocked"}
                onClose={onCancelDialogClose}
                title={t("addStudent.cancelDialogTitle")}
                withCloseButton
            >
                <div className="flex align-center gap-8">
                    <Button
                        variant="outlined"
                        className="contact-information-form__button"
                        onClick={onCancelDialogClose}
                    >
                        {t("button.back")}
                    </Button>
                    <Button
                        variant="contained"
                        className="button-accent contact-information-form__button"
                        onClick={onCancelDialogContinue}
                    >
                        {t("button.continue")}
                    </Button>
                </div>
            </BaseDialog>
        </div>
    );
};

export default AddStudent;
